import { api, writeDB } from './store';
import { Asset } from './user';
import { BankAccount } from './company';
import { PaymentMethod } from './payment';
import { PaginatedLoaderResponse, paginatedLoaderResponseFrom } from './data';
import { apollo } from './graphql';
import { GET_ACTIVITY } from './queries/activity';
import { Product, ProductVariant } from './product';
import { Seat } from './ticket';
import { EventStatus } from './event';

/* INTERFACES */
export type ActivityType = 'change_activity' | 'comment';

// HINT: Sync with back-end's ActivityPolicy.allowed_subjects
export type ActivitySubjectType =
  | 'venue'
  | 'order'
  | 'comment'
  | 'change_activity'
  | 'event'
  | 'merch_product'
  | 'product' // 'event' and 'merch_product' are for querying, but the API returns 'product' for both
  | 'contract'
  | 'company'
  | 'user'
  | 'venue'
  | 'venue_layout'
  | 'shop'
  | 'voucher_campaign';

export interface ActivityUser {
  id: string;
  name: string;
  email: string;
  avatar?: Asset;
}

export const botUser: ActivityUser = {
  id: 'ks-bot',
  name: 'KS Bot',
  email: '',
};

export type Activity =
  | UserAddedToCompanyActivity
  | UserRemovedFromCompanyActivity
  | MembershipChangedEvent
  | InviteUserActivity
  | ClearingCreatedActivity
  | EventSaleChangeActivity
  | ItemRefundActivity
  | ItemDeleteActivity
  | ContingentChangeActivity
  | PaymentAddedActivity
  | PaymentMethodChangeActivity
  | SeatsBlockedActivity
  | SeatsUnBlockedActivity
  | UnbookInvoiceActivity
  | PaypalWebhookActivity
  | GenericActivity
  | ProcessPaymentAuthorizationActivity;

interface BaseActivity {
  id: string;
  content?: string;
  createdAt: Date;
  type: ActivityType;
  $permissions: Array<'update' | 'destroy'>;
  comments?: Activity[];
  user?: ActivityUser;
  recordId?: string;
  subjectType?: ActivitySubjectType;
  subjectId?: string;
  meta?: {
    shipmentRelevant?: boolean;
    productImportant?: boolean;
  };
}

export interface ClearingCreatedActivity extends BaseActivity {
  action: 'clearing_created';
  modifications: {
    from?: Date;
    to?: Date;
  };
}

export interface EventSaleChangeActivity extends BaseActivity {
  action: 'event_sale_started' | 'event_sale_requested' | 'event_sale_stopped';
  modifications: {
    salesStats: {
      cart: number;
      sold: number;
      used: number;
      local: number;
      blocked: number;
      reserved: number;
      available: number;
      soldHard: number;
      soldOnline: number;
      systemFree: number;
      systemSold: number;
      personalized: number;
    };
    publishedFrom: Date;
    publishedUntil: Date;
    statusChangeTo: EventStatus;
    statusChangeFrom: EventStatus;
  };
}

export interface InviteUserActivity extends BaseActivity {
  action: 'invite_user';
  modifications: {
    invitedEmail: string;
    role: string;
  };
}

export interface MembershipChangedEvent extends BaseActivity {
  action: 'membership_changed';
  modifications: {
    // context information (not actual changes)
    userId: string;
    userName?: string;
    email: string;
    // when those are set, means they changed
    role?: string;
    comment?: string;
    permissions?: string[];
  };
}

export interface UserRemovedFromCompanyActivity extends BaseActivity {
  action: 'user_removed_from_company';
  modifications: {
    userId?: string;
    role: string;
    permissions?: Array<string>;
    comment?: string;
    userName?: string;
    email: string;
  };
}

export interface UserAddedToCompanyActivity extends BaseActivity {
  action: 'user_added_to_company';
  modifications: {
    userId: string;
    role: string;
    permissions?: Array<string>;
    comment?: string;
    userName?: string;
    email: string;
  };
}

export interface ItemRefundActivity extends BaseActivity {
  action: 'items_refunded';
  modifications: {
    items: Array<{
      id: string;
      productId: string;
      productVariantId: string;
      refundedById: string;
    }>;
    deliveryId: string;
    invoiceId: string;
  };
}

export interface ActivityItem {
  applicableProductVariantIds: string[];
  available: boolean;
  blockReason: string;
  bundledById: string;
  createdAt: Date;
  deliveryId: string;
  downloadToken: string;
  id: string;
  incomingDeliveryId: string;
  invalidated: boolean;
  invalidatedById: string;
  invoiceId: string;
  orderedAt: Date;
  personalIdentifier: string;
  price: number;
  productId: string;
  productVariantId: string;
  refundedById: string;
  seatId: string;
  storageId: string;
  updatedAt: Date;
  usedAt: Date;
  validationCode: string;
  vatPercentage: number;
  venueSectionId: string;
  seatDetails?: SeatDetail;
}

export interface ActivityModificationItem {
  product: Partial<Product | (Event & { __typename: 'MerchProduct' | 'Event' | 'ShippingProduct' })>;
  productVariant: Partial<ProductVariant>;
  seat: Partial<Seat>;
  total: number;
}

export interface ItemDeleteActivity extends BaseActivity {
  action: 'outgoing_items_removed' | 'incoming_items_removed' | 'auto_cancelled' | 'auto_archived';
  modifications: {
    items?: Array<ActivityItem>;
  };
  content: string;
}

export interface ContingentChangeActivity extends BaseActivity {
  action: 'contingent_change';
  modifications: {
    from: number;
    to: number;
    delta: number;
  };
}

export interface SeatsBlockedActivity extends BaseActivity {
  action: 'seats_blocked';
  subjectType: ActivitySubjectType;
  subjectId: string;
  modifications: {
    itemIds: string[];
    reason: string;
  };
}

export interface SeatsUnBlockedActivity extends BaseActivity {
  action: 'seats_unblocked';
  subjectType: ActivitySubjectType;
  subjectId: string;
  modifications: {
    itemIds: string[];
    reason: string;
  };
}

export interface PaymentMethodChangeActivity extends BaseActivity {
  action: 'preferred_payment_method_updated';
  modifications: {
    from: string;
    to: string;
  };
}

export interface PaymentAddedActivity extends BaseActivity {
  action: 'payment_added_by_admin';
  modifications?: {
    amount: number;
    status: string;
    reference: string;
    bankAccount: BankAccount;
    paymentMethodId: PaymentMethod;
  };
}

export interface UnbookInvoiceActivity extends BaseActivity {
  action: 'unbook_invoice';
  modifications?: {
    previous: {
      bookedAt: string;
      bookingNumber: number;
      pdfAssetId: string;
    };
  };
}

export interface PaypalWebhookActivity extends BaseActivity {
  action: 'paypal_webhook_processed';
  modifications: {
    summary: string;
    resource?: {
      id: string;
    };
  };
}

export interface ProcessPaymentAuthorizationActivity extends BaseActivity {
  action: 'process_payment_authorization';
  modifications: {
    action: 'capture' | 'release';
  };
}

// WIP until we add all the action types
interface GenericActivity extends BaseActivity {
  action:
    | 'invoice_created'
    | 'invoice_booked'
    | 'items_added_to_order'
    | 'cart_created'
    | 'cart_updated'
    | 'comment'
    | 'order_added_to_picklist'
    | 'order_placed'
    | 'stripe_webhook_processed'
    | 'user_changed'
    | 'user_removed'
    | 'stripe_charge_performed'
    | 'marked_problematic'
    | 'unmarked_problematic';
  modifications: unknown;
}

/* ACTIVITIES CREATE */
export async function createActivity(
  subjectId: string,
  subjectType: ActivitySubjectType,
  content: string
): Promise<void> {
  const response = await api.createActivity({
    subjectId,
    subjectType,
    content,
  });

  writeDB.table('orders').insert(response.data);
}

export interface ActivityMetaFilter {
  shipmentRelevant?: boolean;
  productImportant?: boolean;
}

/* ACTIVITIES READ */
export async function getActivities({
  subjectId,
  subjectType,
  page,
  perPage,
  meta,
}: {
  subjectId: string;
  subjectType: ActivitySubjectType;
  term?: string;
  page?: number;
  perPage?: number;
  meta?: ActivityMetaFilter;
}): Promise<PaginatedLoaderResponse> {
  const response = await api.indexActivities({
    page: (page || 0) + 1,
    perPage,
    subjectId,
    subjectType,
    meta,
  });
  writeDB.table('activities').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

export async function getActivityModifications({ id }: { id: string }): Promise<ActivityModificationItem[]> {
  const result = await apollo.query({
    query: GET_ACTIVITY,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (result.data.activity as any).items;
}

// Comments only
export async function updateActivity({
  activityId,
  content,
  meta,
}: {
  activityId: string;
  content?: string;
  meta?: Record<string, unknown>;
}): Promise<void> {
  const response = await api.updateActivity({
    activityId,
    content,
    meta,
  });
  writeDB.table('activities').upsert(response.data);
}

export async function toggleActivityMeta({
  activity,
  metaFieldName,
  onFlag,
}: {
  activity: Activity;
  metaFieldName: string;
  onFlag?: () => void;
}): Promise<void> {
  await updateActivity({
    activityId: activity.id,
    meta: { ...(activity.meta || {}), [metaFieldName]: !activity.meta?.[metaFieldName] },
  });
  onFlag?.();
}

export interface SeatDetail {
  sectionName: string;
  row: string;
  name: string;
}
export function updateDeleteActivity(activityId: string, seatDetails: SeatDetail): void {
  const activity = writeDB.table('activities').find(activityId) as ItemDeleteActivity;
  writeDB.table('activities').update(activityId, {
    modifications: {
      ...activity?.modifications,
      seatDetails,
    },
  });
}

export async function deleteActivity(activityId: string): Promise<void> {
  await api.destroyActivity({ activityId });
  writeDB.table('activities').delete(activityId);
}

export function prettyActivityName(name?: string): string {
  if (!name) {
    return '';
  }
  const modifiedName = name.replace(/_/g, ' ');
  return modifiedName.charAt(0).toUpperCase() + modifiedName.slice(1);
}
